import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  MultiSelect,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Divider,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconFilter,
  IconRefresh,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  useCptCodesAdmin,
  useCreateCptCode,
  useUpdateCptCode,
  useToggleCptCodeActive,
} from "../../../hooks/admin/useCptCodes";
import CptCodeForm, {
  CptCodeFormValues,
} from "../../../components/admin/cptcodes/CptCodeForm";
import { CptCode } from "../../../services/admin/cptcodes.service";

interface CptCodeFilters {
  statusFilter?: string[];
}

const ITEMS_PER_PAGE = 20;

const feeFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const formatFee = (fee: CptCode["fee"]): string => {
  if (fee === null || fee === undefined) return "—";
  const numeric = typeof fee === "string" ? Number(fee) : fee;
  if (!Number.isFinite(numeric)) return "—";
  return feeFormatter.format(numeric);
};

export default function CptCodesAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editing, setEditing] = useState<CptCode | null>(null);
  const [itemToToggle, setItemToToggle] = useState<CptCode | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<CptCodeFilters>({});
  const [pendingFilters, setPendingFilters] = useState<CptCodeFilters>({});
  const [page, setPage] = useState(1);

  // Data hooks
  const { data: cptCodes = [], isLoading, refetch } = useCptCodesAdmin();
  const createCptCode = useCreateCptCode();
  const updateCptCode = useUpdateCptCode();
  const toggleCptCodeActive = useToggleCptCodeActive();

  // Filter & search data
  const filteredData = useMemo(() => {
    let result = cptCodes;

    // Text search
    if (debouncedSearch) {
      const lowerSearch = debouncedSearch.toLowerCase();
      result = result.filter(
        (item) =>
          item.code.toLowerCase().includes(lowerSearch) ||
          item.description.toLowerCase().includes(lowerSearch)
      );
    }

    // Status filter
    if (filters.statusFilter && filters.statusFilter.length > 0) {
      result = result.filter((item) => {
        const status = item.isActive ? "active" : "inactive";
        return filters.statusFilter!.includes(status);
      });
    }

    return result;
  }, [cptCodes, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  // Check if any filters are active
  const hasActiveFilters =
    filters.statusFilter && filters.statusFilter.length > 0;

  // Handlers
  const handleAdd = () => {
    setEditing(null);
    openFormModal();
  };

  const handleEdit = (item: CptCode) => {
    setEditing(item);
    openFormModal();
  };

  const handleSubmit = async (values: CptCodeFormValues) => {
    try {
      if (editing) {
        await updateCptCode.mutateAsync({
          id: editing.id,
          ...values,
        });
      } else {
        await createCptCode.mutateAsync(values);
      }
      closeFormModal();
      setEditing(null);
    } catch {
      // Error already handled by mutation's onError callback
    }
  };

  const handleToggleActive = (item: CptCode) => {
    setItemToToggle(item);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (itemToToggle) {
      await toggleCptCodeActive.mutateAsync({
        id: itemToToggle.id,
        isActive: !itemToToggle.isActive,
      });
      closeConfirmModal();
      setItemToToggle(null);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({});
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>CPT Codes</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage CPT codes for medical procedures and billing
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by code or description..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.statusFilter &&
                      filters.statusFilter.length > 0 && (
                        <Badge variant="light" color="blue">
                          {filters.statusFilter.length} Status
                          {filters.statusFilter.length > 1 ? "es" : ""}
                        </Badge>
                      )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} code
                  {filteredData.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  CPT Codes ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No CPT codes match the current filters"
                    : "No CPT codes found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 140 }}>Code</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th style={{ width: 120, textAlign: "right" }}>
                        Fee
                      </Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ opacity: item.isActive ? 1 : 0.5 }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={600} ff="monospace">
                            {item.code}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {item.description || "\u2014"}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: "right" }}>
                          <Text
                            size="sm"
                            ff="monospace"
                            c={item.fee === null ? "dimmed" : undefined}
                          >
                            {formatFee(item.fee)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={item.isActive ? "green" : "red"}
                            variant="light"
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={item.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={item.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(item)}
                              >
                                {item.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal.Root
          opened={formModalOpened}
          onClose={() => {
            closeFormModal();
            setEditing(null);
          }}
          size="md"
          centered
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {editing ? "Edit CPT Code" : "Add New CPT Code"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>

            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <CptCodeForm
                key={editing?.id ?? (formModalOpened ? "new" : "closed")}
                initialValues={editing || undefined}
                onSubmit={handleSubmit}
              />
            </Modal.Body>

            <Divider />
            <Group justify="space-between" p="md">
              <Button
                variant="default"
                onClick={() => {
                  closeFormModal();
                  setEditing(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="cpt-code-form"
                loading={createCptCode.isPending || updateCptCode.isPending}
              >
                {editing ? "Update" : "Create"}
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter CPT Codes"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <MultiSelect
              label="Status"
              placeholder="Select status..."
              data={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              value={pendingFilters.statusFilter || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  statusFilter: values.length > 0 ? values : undefined,
                })
              }
              clearable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Toggle Active Confirmation Modal */}
        <Modal
          opened={confirmModalOpened}
          onClose={() => {
            closeConfirmModal();
            setItemToToggle(null);
          }}
          title={
            itemToToggle?.isActive ? "Deactivate CPT Code" : "Activate CPT Code"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {itemToToggle?.isActive ? "deactivate" : "activate"} CPT code{" "}
              <strong>{itemToToggle?.code}</strong>?
            </Text>
            {itemToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated codes will no longer be available for selection in
                stains, diagnoses, or billing.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  closeConfirmModal();
                  setItemToToggle(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color={itemToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleCptCodeActive.isPending}
              >
                {itemToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
